/**
 * coachMarkSequence - the pure step state machine behind the onboarding coach
 * marks (src/ui/onboarding/coachMarks.js).
 *
 * A sequence is an ordered list of steps, each declaring the event name that
 * completes it ({id, completesOn}). notify(eventName) advances past the
 * CURRENT step only when the name matches - later steps' events are ignored
 * until it's their turn, so a user who clicks an exhibit before learning to
 * walk still gets the walk lesson.
 *
 * Pure, framework-free module: no DOM, no Babylon.
 */

/**
 * @typedef {Object} CoachStep
 * @property {string} id
 * @property {string} completesOn - event name that completes this step
 */

/**
 * @param {CoachStep[]} steps
 */
export function createSequence(steps) {
  const all = [...steps];
  let index = 0;
  let done = all.length === 0;
  let skipped = false;
  /** @type {Set<(current: CoachStep|null, done: boolean) => void>} */
  const listeners = new Set();

  const current = () => (done ? null : all[index]);

  const notifyChange = () => {
    listeners.forEach((fn) => {
      try {
        fn(current(), done);
      } catch (e) {
        console.warn('coachMarkSequence listener failed:', e);
      }
    });
  };

  return {
    current,
    isDone: () => done,
    wasSkipped: () => skipped,
    stepIndex: () => index,
    stepCount: () => all.length,

    /**
     * Report an observed event. Advances only when it matches the current
     * step's completesOn.
     * @param {string} eventName
     * @returns {boolean} whether the sequence advanced
     */
    notify(eventName) {
      if (done) return false;
      if (all[index].completesOn !== eventName) return false;
      return this.advance();
    },

    /**
     * Unconditionally complete the CURRENT step (used by auto-advance
     * timers, e.g. the follow-mode card). Distinct from skip(): a sequence
     * that finishes through advance() completed - wasSkipped() stays false.
     * @returns {boolean} whether the sequence advanced
     */
    advance() {
      if (done) return false;
      index++;
      if (index >= all.length) done = true;
      notifyChange();
      return true;
    },

    /** Abandon the rest of the sequence. */
    skip() {
      if (done) return;
      done = true;
      skipped = true;
      notifyChange();
    },

    /**
     * @param {(current: CoachStep|null, done: boolean) => void} fn
     * @returns {() => void} unsubscribe
     */
    onChange(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}
